import { brandPalette, secondaryPalette } from './palette'

/**
 * Any values that aren't sensitive and don't change between deployments
 * (otherwise they belong in .env)
 */

/**
 * Number of quotes allowed on trial accounts
 */
export const TRIAL_QUOTE_LIMIT = 15

/**
 * Number of files allowed on a quote
 */
export const TRIAL_QUOTE_FILES_LIMIT = 5

/**
 * REGEX to match valid UUID
 */
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/**
 * Our current quote version
 */
export const CURRENT_QUOTE_VERSION = 13

/**
 * Corrective constant for rounding re: https://github.com/PaintScout/PaintScout/issues/11821
 */
export const CORRECTIVE_CONSTANT = 0.0000000001 // Adjusts for odd floating point behavior

const userColorEntry = (hue: keyof typeof secondaryPalette) => ({
  light: secondaryPalette[hue].C90,
  dark: secondaryPalette[hue].C30,
  // Chip palette (per design): bg = C95, accent/pill = C50, text = C40.
  chipBg: secondaryPalette[hue].C95,
  chipPill: secondaryPalette[hue].C50,
  chipText: secondaryPalette[hue].C40
})

export const USER_COLOR_MAP = {
  [secondaryPalette['20'].C60]: userColorEntry('20'),
  [secondaryPalette['50'].C60]: userColorEntry('50'),
  [secondaryPalette['80'].C60]: userColorEntry('80'),
  [secondaryPalette['110'].C60]: userColorEntry('110'),
  [secondaryPalette['140'].C60]: userColorEntry('140'),
  [secondaryPalette['170'].C60]: userColorEntry('170'),
  [secondaryPalette['200'].C60]: userColorEntry('200'),
  [secondaryPalette['230'].C60]: userColorEntry('230'),
  [secondaryPalette['260'].C60]: userColorEntry('260'),
  [secondaryPalette['290'].C60]: userColorEntry('290'),
  [secondaryPalette['320'].C60]: userColorEntry('320'),
  [secondaryPalette['350'].C60]: userColorEntry('350')
}

export const USER_COLOR_PALETTE = [
  secondaryPalette['260'].C60,
  secondaryPalette['140'].C60,
  secondaryPalette['50'].C60,
  secondaryPalette['290'].C60,
  secondaryPalette['230'].C60,
  secondaryPalette['170'].C60,
  secondaryPalette['350'].C60,
  secondaryPalette['110'].C60,
  secondaryPalette['200'].C60,
  secondaryPalette['20'].C60,
  secondaryPalette['80'].C60,
  secondaryPalette['320'].C60
]

export const USER_COLOR_PALETTE_CHROMA_ORDER = [
  secondaryPalette['20'].C60,
  secondaryPalette['50'].C60,
  secondaryPalette['80'].C60,
  secondaryPalette['110'].C60,
  secondaryPalette['140'].C60,
  secondaryPalette['170'].C60,
  secondaryPalette['200'].C60,
  secondaryPalette['230'].C60,
  secondaryPalette['260'].C60,
  secondaryPalette['290'].C60,
  secondaryPalette['320'].C60,
  secondaryPalette['350'].C60
]

export const TAG_COLORS = [
  [brandPalette.Neutral.P60, brandPalette.Neutral.P96],
  [secondaryPalette['20'].C60, secondaryPalette['20'].C95],
  [secondaryPalette['50'].C60, secondaryPalette['50'].C95],
  [secondaryPalette['80'].C60, secondaryPalette['80'].C95],
  [secondaryPalette['110'].C60, secondaryPalette['110'].C95],
  [secondaryPalette['140'].C60, secondaryPalette['140'].C95],
  [secondaryPalette['170'].C60, secondaryPalette['170'].C95],
  [secondaryPalette['200'].C60, secondaryPalette['200'].C95],
  [secondaryPalette['230'].C60, secondaryPalette['230'].C95],
  [secondaryPalette['260'].C60, secondaryPalette['260'].C95],
  [secondaryPalette['290'].C60, secondaryPalette['290'].C95],
  [secondaryPalette['320'].C60, secondaryPalette['320'].C95],
  [secondaryPalette['350'].C60, secondaryPalette['350'].C95],
  [brandPalette.Neutral.P40, brandPalette.Neutral.P94]
].map((pair) => {
  return [pair[0].toUpperCase(), pair[1].toUpperCase()]
})

export const CREW_COLORS = [
  [secondaryPalette['20'].C60, secondaryPalette['20'].C95],
  [secondaryPalette['50'].C60, secondaryPalette['50'].C95],
  [secondaryPalette['80'].C60, secondaryPalette['80'].C95],
  [secondaryPalette['110'].C60, secondaryPalette['110'].C95],
  [secondaryPalette['140'].C60, secondaryPalette['140'].C95],
  [secondaryPalette['170'].C60, secondaryPalette['170'].C95],
  [secondaryPalette['200'].C60, secondaryPalette['200'].C95],
  [secondaryPalette['230'].C60, secondaryPalette['230'].C95],
  [secondaryPalette['260'].C60, secondaryPalette['260'].C95],
  [secondaryPalette['290'].C60, secondaryPalette['290'].C95],
  [secondaryPalette['320'].C60, secondaryPalette['320'].C95],
  [secondaryPalette['350'].C60, secondaryPalette['350'].C95]
].map((pair) => {
  return [pair[0].toUpperCase(), pair[1].toUpperCase()]
})

// going to try not using these for now, rainbow calendar/user colors above only
// export const USER_ACTIVITY_COLOR_PALETTE = [
// '#929fb5',
// '#c6cedc',
// '#55647c',
// '#d9dee8',
// '#2e3b51',
// '#e8ebf1',
// '#738198',
// '#b4bed0',
// '#252e3d',
// '#a2afc3',
// '#394860'
// ]

export const PRESENTATION_LABEL = 'Presentation'
export const PRESENTATION_LABEL_PLURAL = 'Presentations'

// User-facing names for the two levels of a PresentationNext doc (#21530).
// Deliberately named by structural role rather than the code terms, because the
// vocabulary is inverted: a data-model `page` (presentation.pages[]) is shown to
// users as a "Section", and a data-model `section` (pages[].sections[]) as a "Block".
export const PRESENTATION_TOP_LEVEL_LABEL = 'Section' // a `page` — top-level unit, becomes a nav item
export const PRESENTATION_TOP_LEVEL_LABEL_PLURAL = 'Sections'
export const PRESENTATION_CONTENT_UNIT_LABEL = 'Block' // a `section` — a content unit within a page
export const PRESENTATION_CONTENT_UNIT_LABEL_PLURAL = 'Blocks'
